import { useMemo } from "react";

/**
 * Compute the 3 hero "vital signs" — small at-a-glance facts shown
 * in the Today tab. Pulled out of VerdictPage so multiple pages can share.
 *
 * Returns:
 *   { nextRain: { label, sub, hasRain }, peak: { label, sub, value }, sun: { label, sub, isSet } }
 */
export function buildVitalSigns(forecast) {
  // Prefer Tomorrow.io 1-min nowcast if present (60 entries, mm/h units),
  // else fall back to Open-Meteo 15-min (16 entries, mm-per-bucket units).
  const tio = forecast?.minutely_1;
  const useTio = !!(tio && (tio.precipitation || []).length);
  const minutely = useTio
    ? tio.precipitation || []
    : forecast?.minutely_15?.precipitation || [];
  const bucketMin = useTio ? 1 : 15;
  const startThr = useTio ? 0.5 : 0.15;
  const hourlyProb = forecast?.hourly?.precipitation_probability || [];
  const sunsetIso = forecast?.daily?.sunset?.[0];
  const sunriseIso = forecast?.daily?.sunrise?.[0];

  // 1) NEXT RAIN — when does meaningful rain first appear?
  let nextRainLabel = "6h+ away";
  let nextRainSub = "no rain";
  let nextRainFound = false;
  for (let i = 0; i < minutely.length; i++) {
    if ((minutely[i] || 0) >= startThr) {
      const minutesAhead = (i + 1) * bucketMin;
      if (minutesAhead < 60) {
        nextRainLabel = `${minutesAhead}m`;
      } else {
        const h = Math.floor(minutesAhead / 60);
        const m = minutesAhead % 60;
        nextRainLabel = m === 0 ? `${h}h` : `${h}h ${m}m`;
      }
      nextRainSub = "incoming";
      nextRainFound = true;
      break;
    }
  }
  if (!nextRainFound) {
    const hourlyPrecip = forecast?.hourly?.precipitation || [];
    for (let i = 0; i < Math.min(12, hourlyPrecip.length); i++) {
      const mm = hourlyPrecip[i] || 0;
      const prob = hourlyProb[i] || 0;
      const significant = mm > 0.1 || (prob >= 40 && mm > 0);
      if (significant) {
        nextRainLabel = `${i + 1}h`;
        nextRainSub = mm >= 1 ? "incoming" : "possible";
        nextRainFound = true;
        break;
      }
    }
  }
  if (!nextRainFound) {
    const peakProb12h = Math.max(
      0,
      ...(forecast?.hourly?.precipitation_probability || [])
        .slice(0, 12)
        .filter((v) => v != null),
    );
    nextRainSub = peakProb12h < 15 ? "stays dry" : "low odds";
  }

  // 2) PEAK CHANCE — max hourly precipitation_probability over next 6h.
  const next6Probs = hourlyProb.slice(0, 6).filter((v) => v != null);
  const peakProb = next6Probs.length ? Math.max(...next6Probs) : null;
  const peakLabel = peakProb != null ? `${Math.round(peakProb)}%` : "—";
  const peakSub =
    peakProb == null
      ? ""
      : peakProb < 10
        ? "stays dry"
        : peakProb < 30
          ? "low odds"
          : peakProb < 60
            ? "some chance"
            : "likely";

  // 3) SUN — show whichever celestial event is next (sunset before sunrise tomorrow)
  let sunLabel = "—";
  let sunSub = "sunset";
  let sunIsSet = true;
  const now = new Date();
  if (sunsetIso) {
    const sunset = new Date(sunsetIso);
    if (sunset > now) {
      sunLabel = sunset.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      sunSub = "sunset";
      sunIsSet = true;
    } else if (sunriseIso) {
      const sunrise = new Date(sunriseIso);
      sunLabel = sunrise.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      sunSub = "sunrise";
      sunIsSet = false;
    }
  }

  return {
    nextRain: {
      label: nextRainLabel,
      sub: nextRainSub,
      hasRain: nextRainSub === "incoming" || nextRainSub === "possible",
    },
    peak: { label: peakLabel, sub: peakSub, value: peakProb },
    sun: { label: sunLabel, sub: sunSub, isSet: sunIsSet },
  };
}

/**
 * Build a short, friendly headline + sub-line based on the forecast.
 * This is the single "answer" the user came for. No jargon.
 */
export function useVerdict(forecast) {
  return useMemo(() => {
    if (!forecast) return { headline: "Loading forecast…", sub: "" };
    // Prefer Tomorrow.io 1-min nowcast if available (mm/h units, 60 entries).
    // This gives a much sharper "rain starts in N minutes" verdict for the
    // critical 0–60 min window. Falls back to Open-Meteo 15-min mm-buckets.
    const tio = forecast.minutely_1;
    const useTio = !!(tio && (tio.precipitation || []).length);
    const minutely = useTio
      ? (tio.precipitation || []).slice(0, 60) // next 60 min @ 1-min
      : (forecast.minutely_15?.precipitation || []).slice(0, 24); // next 6h @ 15min
    const bucketMin = useTio ? 1 : 15;
    const startThr = useTio ? 0.5 : 0.15; // mm/h vs mm-per-bucket
    const nextHours = (forecast.hourly?.precipitation || []).slice(0, 6);
    const cur = forecast.current || {};
    const curPrecip = cur.precipitation || 0;

    // First slot with meaningful rain
    const firstRainIdx = minutely.findIndex((v) => (v || 0) >= startThr);
    const etaMin = firstRainIdx >= 0 ? firstRainIdx * bucketMin : null;

    const peak6h = nextHours.length ? Math.max(...nextHours.map((v) => v || 0)) : 0;
    const total6h = nextHours.reduce((s, v) => s + (v || 0), 0);

    // Already raining now?
    if (curPrecip >= 0.2) {
      if (peak6h >= 5) {
        return {
          headline: "Raining · heavier bursts ahead",
          sub: `Steady shower now, ${peak6h.toFixed(1)} mm/h peak within 6 hours.`,
          mood: "rain",
        };
      }
      return {
        headline: "Raining right now",
        sub: "Light shower — should ease within the hour.",
        mood: "rain",
      };
    }

    // Rain imminent
    if (etaMin != null && etaMin <= 60) {
      return {
        headline: etaMin === 0 ? "Rain starting now" : `Rain in ~${etaMin} min`,
        sub: peak6h >= 5
          ? "Grab an umbrella — it may get heavy."
          : "Light rain expected. An umbrella would help.",
        mood: "rain",
      };
    }

    // Rain later in window
    if (total6h >= 2) {
      const startIdx = nextHours.findIndex((v) => (v || 0) >= 0.2);
      const hrs = startIdx >= 0 ? startIdx : 2;
      return {
        headline: `Rain expected in ~${hrs}h`,
        sub: `About ${total6h.toFixed(1)} mm total over the next 6 hours.`,
        mood: "rain",
      };
    }

    if (total6h >= 0.3) {
      return {
        headline: "Light showers possible",
        sub: "Mostly dry, with a brief shower chance in the next 6 hours.",
        mood: "cloudy",
      };
    }

    // Dry
    const code = cur.weather_code;
    if (code === 0 || code === 1) {
      return {
        headline: "Clear for the next 6 hours",
        sub: "No rain in sight — great time to be outside.",
        mood: "clear",
      };
    }
    if (code === 2 || code === 3) {
      return {
        headline: "Cloudy but dry",
        sub: "Overcast skies, no rain expected in 6 hours.",
        mood: "cloudy",
      };
    }
    return {
      headline: "Dry for the next 6 hours",
      sub: "Rain isn't forecast in your area.",
      mood: "clear",
    };
  }, [forecast]);
}
